import { Component, inject, input, OnInit } from '@angular/core';
import { ButtonComponent } from '../../../../../common/components/button/button.component';
import { DeviceService } from '../../../../../services/devices/device.service';
import { MatIcon } from '@angular/material/icon';
import { DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DialogService } from '../../../../../services/dialog/dialog.service';
import { filter, first, Observable, switchMap } from 'rxjs';
import { SnackbarService } from '../../../../../services/snackbar/snackbar.service';
import { DeviceCardTestIds } from './device-card.test-ids';
import { MatTooltip } from '@angular/material/tooltip';

@Component({
  selector: 'app-device-card',
  imports: [ButtonComponent, MatIcon, FormsModule, DatePipe, MatTooltip],
  templateUrl: './device-card.component.html',
  styleUrl: './device-card.component.scss'
})
/**
 * Mit dieser Komponente wird das DeviceManagement für einzelne SB-User umgesetzt. Speziell wird hier eine Komponente
 * erstellt, die einzelne Devices in einer Liste anzeigen und managebar machen.
 * Hierdurch erfüllte AFOs:
 * A_25395-03 - Matrix Module (Device Management)
 */
export class DeviceCardComponent implements OnInit {
  protected readonly testIds = DeviceCardTestIds;
  public deviceId = input<string>('');
  public displayName = input<string>('');
  public lastSeenIp = input<string>('');
  public lastSeenTs = input<number>(0);
  public editingMode = false;
  public tempDisplayName = '';
  public lastSeenDate: Date | null = null;
  public isVerified = false;

  public readonly deviceService: DeviceService = inject(DeviceService);
  private readonly dialogService: DialogService = inject(DialogService);
  private readonly snackbarService: SnackbarService = inject(SnackbarService);

  public ngOnInit(): void {
    this.tempDisplayName = this.displayName();
    this.lastSeenDate = new Date(this.lastSeenTs());
    (async (): Promise<void> => {
      const verificationStatus = await this.deviceService.isVerified(this.deviceId());
      if (verificationStatus) {
        this.isVerified = verificationStatus.crossSigningVerified ?? false;
      }
    })();
  }

  public getLastSeenIp(inputIp: string): string {
    if (inputIp && inputIp !== '') {
      return inputIp;
    } else {
      return 'None';
    }
  }

  public startEditingMode(): void {
    this.editingMode = true;
  }

  public async saveChanges(): Promise<void> {
    await this.deviceService.setDeviceDetails(this.deviceId(), this.tempDisplayName);
    this.editingMode = false;
  }

  public cancelChanges(): void {
    this.tempDisplayName = this.displayName();
    this.editingMode = false;
  }

  public deleteDevice(): void {
    const activeDevice = this.deviceService.getActiveDevice();

    if (this.deviceId() === activeDevice) {
      this.snackbarService.error('Sie können nicht das Device, mit dem Sie eingeloggt sind, abmelden');
      return;
    }
    this.openWarningDialog()
      .pipe(
        filter(confirmed => confirmed),
        switchMap(() => this.deviceService.deleteDevice(this.deviceId()))
      )
      .pipe(first())
      .subscribe();
  }

  private openWarningDialog(): Observable<boolean> {
    return this.dialogService
      .openWarningDialog({
        title: 'SICHER ABMELDEN?',
        message: 'Wollen Sie das Gerät wirklich abmelden?',
        confirmText: 'Ja',
        cancelText: 'Nein',
        cancelDataTestId: 'button_cancel_devicedeletion',
        confirmDataTestId: 'button_confirm_devicedeletion',
        dialogContentTestId: 'dialog_devicedeletion_content',
        dialogActionTestId: 'dialog_devicedeletion'
      })
      .pipe(first());
  }
}
